import React, { useState, useEffect, useRef } from "react";
import _ from "lodash";
import { IAppPropsWithRoute } from "../../../common/types";
import { withRouter } from "react-router-dom";
import { useDispatch } from "react-redux";
import Navbar from "../../shared/Navbar";
import { MuiThemeProvider, Theme, Paper, CircularProgress, Typography } from "@material-ui/core";
import ButtonsNav from "./ButtonsNav";
import ErrorTryAgain from "../../shared/ErrorTryAgain/ErrorTryAgain";
import RouteInfo from "./RouteInfo";
import SMAButton from "../../shared/Button/Button";
import { makeStyles } from "@material-ui/core/styles";
import themeDark from "../../shared/themeDark";
import EditDetailsModal from "./EditDetailsModal";
import StartScreen from "./StartScreen";
import api from "../../../store/api";
import DateFnsUtils from "@date-io/date-fns";
import { EVENT_SHIFT_STATUS, OTHER_LOCATION, Location, StartLocation, ReturnLocation, CustomStartLocation, timeString } from "./types";
import { showNavbar } from "../../../store/modules/appStateSlice";

const FETCH_ETA_INTERVAL = 2 * 1000 * 60; // 2 mins

interface EventMedicalPageProps extends IAppPropsWithRoute {}

type dateTimeDetails = {
  enrouteDate: string | null;
  onSiteDate: string | null;
  completedDate: string | null;
  enRouteBackDate: string | null;
};

const useStyles = makeStyles((theme: Theme) => ({
  root: (theme: Theme) => ({
    display: "flex",
    backgroundColor: theme.palette.background.paper,
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "center",
    borderRadius: 0,
    boxShadow: "none",
    flex: "1 0",
  }),
  progress: (theme: Theme) => ({
    backgroundColor: theme.palette.background.paper,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 0,
    flex: "1 0",
    width: "100%",
  }),
  startButton: {},
  editDetailsButton: {
    marginTop: "15px",
  },
  headerTitle: {
    fontFamily: "Lato",
    fontSize: "18px",
    fontWeight: 400,
    margin: "19px 0 0 29px",
  },
}));

const formatEtaString = (timeStarted: string, etaValue: number) => {
  const timeStartedParsed = Date.parse(timeStarted);
  const etaDate = new Date(timeStartedParsed + etaValue * 1000);
  const hrs = etaDate.getHours();
  const mins = etaDate.getMinutes();
  return `${hrs < 10 ? "0" : ""}${hrs}:${mins < 10 ? "0" : ""}${mins}`;
};

const EventMedicalPage: React.FC<EventMedicalPageProps> = () => {
  const classes = useStyles(themeDark);
  const dateUtils = new DateFnsUtils();

  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [shiftState, setShiftState] = useState(EVENT_SHIFT_STATUS.Initial);
  const [eventLocationOptionList, setEventLocationOptionList] = useState<Location[]>([]);
  const [shiftId, setShiftId] = useState("");
  const [eventLocation, setEventLocation] = useState<StartLocation>();
  const [customEventLocation, setCustomEventLocation] = useState<CustomStartLocation>();
  const [returnLocation, setReturnLocation] = useState<ReturnLocation>();
  const [timeOnsite, setTimeOnsite] = useState<Date | null>(null);
  const [eta, setEta] = useState("");

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const dispatch = useDispatch();

  useEffect(() => {
    (async function () {
      dispatch(showNavbar(false));
      if (eventLocationOptionList.length === 0) {
        try {
          const resp = await api.getCustomerLocations("version="+Math.random().toString()+Math.random().toString()+Math.random().toString());
          const locationsList: Location[] = resp?.data?.model;
          setEventLocationOptionList([...locationsList, OTHER_LOCATION]);

          const respSD = await api.getActiveEventShiftDetails("version="+Math.random().toString()+Math.random().toString()+Math.random().toString());
          const respShiftId = respSD?.eventShiftId;
          setShiftId(respShiftId);

          await fetchShiftDetails(respShiftId, [...locationsList]);
          setIsLoading(false);
        } catch (e) {
          setIsError(true);
          console.log(e);
        }
      }
    })();
  }, []);

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current); // clear interaval on component unmount
      }
    };
  }, []);

  const fetchETA = async (shiftId: string) => {
    try {
      const shiftDetails = await api.getEventShiftDetails(`eventShiftId=${shiftId}`+"&version="+Math.random().toString()+Math.random().toString()+Math.random().toString());
      const timeStarted = shiftDetails?.model?.startedDate;
      const etaValue = shiftDetails?.model?.etaValue || shiftDetails?.model?.tomTomRouteETA?.routes[0]?.summary?.travelTimeInSeconds || 0;
      const etaTimeStr = formatEtaString(timeStarted, etaValue);
      setEta(etaTimeStr);
    } catch (e) {
      console.log(e);
    }
  };

  const changeShiftState = async (newStatus: number) => {
    setIsLoading(true);

    let dateTime: dateTimeDetails = { enRouteBackDate: null, enrouteDate: null, onSiteDate: null, completedDate: null };
    const statusUpdate = newStatus
    if (newStatus === shiftState) {
      // click on a current active button
      if (newStatus !== EVENT_SHIFT_STATUS.EnRouteToSite) {
        if (newStatus === EVENT_SHIFT_STATUS.OnSite) {
          dateTime.onSiteDate = new Date().toISOString();
        } else if (newStatus === EVENT_SHIFT_STATUS.EventComplete) {
          dateTime.completedDate = new Date().toISOString();
        } else if (newStatus === EVENT_SHIFT_STATUS.EnRouteBack) {
          dateTime.enRouteBackDate = new Date().toISOString();
        }
        newStatus -= 1; // go back on 1 status
      }
    }

    if (newStatus === EVENT_SHIFT_STATUS.EnRouteToSite) {
      fetchETA(shiftId);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      intervalRef.current = setInterval(() => {
        fetchETA(shiftId);
      }, FETCH_ETA_INTERVAL); // fetch ETA value every 2 mins
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current); // clear interaval on component unmount
      }
    }

    try {
      await api.updateEventShiftStatus("version="+Math.random().toString()+Math.random().toString()+Math.random().toString(), {
        eventShiftId: shiftId,
        status: statusUpdate,
        ...dateTime,
      });
      if (newStatus === EVENT_SHIFT_STATUS.ArrivedBack) {
        // arrived back -> return to inital state
        setShiftState(EVENT_SHIFT_STATUS.Initial);
        setShiftId("");
        setEventLocation({} as StartLocation);
        setCustomEventLocation({} as CustomStartLocation);
        setReturnLocation({} as ReturnLocation);
        setTimeOnsite(null);
        setEta("");
      } else {
        setShiftState(newStatus);
      }
    } catch (e) {
      setIsError(true);
    }
    setIsLoading(false);
  };

  const fetchShiftDetails = async (rshiftId: string, locList: Location[]) => {
    const shiftDetails = await api.getEventShiftDetails(`eventShiftId=${rshiftId}`+"&version="+Math.random().toString()+Math.random().toString()+Math.random().toString());
    if (shiftDetails?.model?.status > EVENT_SHIFT_STATUS.Started) {
      // old session
      const locId = shiftDetails?.model?.eventLocationId;
      setEventLocation({
        id: locId,
        name: _.find(locList, { value: locId })?.text ?? "",
      });
      if (!locId) {
        setCustomEventLocation({
          eventLocationGooglePlaceId: shiftDetails?.model?.eventLocationGooglePlaceId,
          eventLocationLatitude: shiftDetails?.model?.eventLocationLatitude,
          eventLocationLongitude: shiftDetails?.model?.eventLocationLongitude,
        });
      }
      setReturnLocation({
        returnLocation: shiftDetails?.model?.returnLocation,
        returnLocationGooglePlaceId: shiftDetails?.model?.returnLocationGooglePlaceId,
        returnLocationLatitude: shiftDetails?.model?.returnLocationLatitude,
        returnLocationLongitude: shiftDetails?.model?.returnLocationLongitude,
      });
      const timeParsed = dateUtils.parse(shiftDetails?.model?.timeOnsite, "HH:mm:ss");
      setTimeOnsite(timeParsed);

      if (shiftDetails?.model?.status === EVENT_SHIFT_STATUS.EnRouteToSite) {
        const timeStarted = shiftDetails?.model?.startedDate;
        const etaValue = shiftDetails?.model?.etaValue || shiftDetails?.model?.tomTomRouteETA?.routes[0]?.summary?.travelTimeInSeconds || 0;
        const etaTimeStr = formatEtaString(timeStarted, etaValue);
        setEta(etaTimeStr);
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
        intervalRef.current = setInterval(() => {
          fetchETA(rshiftId);
        }, FETCH_ETA_INTERVAL); // fetch ETA value every 2 mins
      }

      setShiftState(shiftDetails?.model?.status);
      return true;
    }
    return false;
  };

  const startShiftAction = async () => {
    setIsLoading(true);
    try {
      const resp = await api.addEventShift("version="+Math.random().toString()+Math.random().toString()+Math.random().toString(), {});
      const isError = resp.hasError;
      if (isError) {
        setErrorText(resp.message);
        throw new Error();
      }

      const respShiftId = resp?.eventShiftId;
      setShiftId(respShiftId);

      const shiftStarted = await fetchShiftDetails(respShiftId, eventLocationOptionList);
      if (!shiftStarted) {
        // start a new session
        setIsEditModalOpen(true);
      }
    } catch (e) {
      setIsError(true);
    }
    setIsLoading(false);
  };

  const EventShiftTitle = (
    <Typography variant="h1" className={classes.headerTitle}>
      Event Shift
    </Typography>
  );

  return (
    <MuiThemeProvider theme={themeDark}>
      <Navbar />
      {isLoading && (
        <Paper className={classes.progress}>
          <CircularProgress />
        </Paper>
      )}
      {isError && <ErrorTryAgain label={errorText} />}

      {!isLoading && !isError && (
        <>
          {shiftState === EVENT_SHIFT_STATUS.Initial && <StartScreen onStart={startShiftAction} />}

          {shiftState !== EVENT_SHIFT_STATUS.Initial && (
            <Paper className={classes.root}>
              <ButtonsNav changeShiftState={changeShiftState} shiftState={shiftState} />

              <RouteInfo
                info={{
                  EventLocation: eventLocation?.name || "",
                  ReturnLocation: returnLocation?.returnLocation || "",
                  TimeRequired: timeString(timeOnsite),
                  ETA: shiftState === EVENT_SHIFT_STATUS.EnRouteToSite || shiftState === EVENT_SHIFT_STATUS.Started ? eta : "",
                }}
              />

              <SMAButton
                className={classes.editDetailsButton}
                color="primary"
                variant="text"
                onAction={() => {
                  setIsEditModalOpen(true);
                }}
              >
                Edit Event Details
              </SMAButton>
            </Paper>
          )}

          <EditDetailsModal
            isOpen={isEditModalOpen}
            onClose={() => {
              setIsEditModalOpen(false);
            }}
            eventLocationOptionList={eventLocationOptionList}
            shiftId={shiftId}
            eventLocation={eventLocation}
            setEventLocation={setEventLocation}
            returnLocation={returnLocation}
            setReturnLocation={setReturnLocation}
            timeOnsite={timeOnsite}
            setTimeOnsite={setTimeOnsite}
            customEventLocation={customEventLocation}
            setCustomEventLocation={setCustomEventLocation}
            initState={shiftState === EVENT_SHIFT_STATUS.Initial}
            onSave={() => {
              setIsEditModalOpen(false);
              if (shiftState === EVENT_SHIFT_STATUS.Initial) {
                _.delay(() => setShiftState(EVENT_SHIFT_STATUS.Started), 100);
                fetchETA(shiftId);
                if (intervalRef.current) {
                  clearInterval(intervalRef.current);
                }
                intervalRef.current = setInterval(() => {
                  fetchETA(shiftId);
                }, FETCH_ETA_INTERVAL); // fetch ETA value every 2 mins
              }
            }}
          />
        </>
      )}
    </MuiThemeProvider>
  );
};

export default withRouter(EventMedicalPage);
